import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import api from "../api/client";
import { POLICY_HINT, checkPassword } from "../utils/passwordPolicy";

/* Public landing for the reset-link email. Token comes from the
 * ``?token=`` query param the bootstrap email links to. Two password
 * fields (must match) keep typos from locking the user out.
 *
 * Backend enforces all the security invariants (token valid, unused,
 * unexpired, 10+ chars, different from current) — the frontend just
 * surfaces the errors and a "log in now" link on success.
 */

export default function ResetPassword() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = (params.get("token") || "").trim();

  const [pw1, setPw1] = useState("");
  const [pw2, setPw2] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);

  if (!token) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center p-4">
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-sm p-8">
          <h1 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-2">Invalid reset link</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
            The link is missing the reset token. Request a new one if needed.
          </p>
          <Link to="/forgot-password" className="text-sm text-blue-600 dark:text-blue-400 hover:underline">
            Request a new reset link →
          </Link>
        </div>
      </div>
    );
  }

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    if (pw1 !== pw2) {
      setError("Passwords don't match.");
      return;
    }
    const problem = checkPassword(pw1);
    if (problem) {
      setError(problem);
      return;
    }
    setBusy(true);
    try {
      await api.post("/api/v1/auth/reset-password", { token, new_password: pw1 });
      setDone(true);
      setTimeout(() => navigate("/login"), 2500);
    } catch (err: any) {
      setError(err.response?.data?.detail || "Reset failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-900 flex items-center justify-center p-4">
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-sm p-8">
        <h1 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-1">Choose a new password</h1>
        <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">
          {POLICY_HINT} The link is single-use; after submitting you'll be redirected to
          login.
        </p>

        {done ? (
          <div className="bg-green-50 dark:bg-green-900/40 border border-green-200 dark:border-green-800 rounded-lg p-3 text-sm text-green-800 dark:text-green-300">
            Password updated. Redirecting to login…
          </div>
        ) : (
          <form onSubmit={onSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">New password</label>
              <input
                type="password"
                value={pw1}
                onChange={(e) => setPw1(e.target.value)}
                required
                autoFocus
                minLength={10}
                className="w-full border border-gray-300 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Confirm password
              </label>
              <input
                type="password"
                value={pw2}
                onChange={(e) => setPw2(e.target.value)}
                required
                minLength={10}
                className="w-full border border-gray-300 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            {error && (
              <p className="text-sm text-red-500 dark:text-red-400 bg-red-50 dark:bg-red-900/40 px-3 py-2 rounded-lg">{error}</p>
            )}
            <button
              type="submit"
              disabled={busy}
              className="w-full bg-blue-600 text-white py-2.5 rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50"
            >
              {busy ? "Saving…" : "Set new password"}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
